// @givmo/design-system — Select (ports the Flutter webapp `BaseDropdownButton`,
// buttons/base_dropdown_button.dart).
//
// Built on Base UI's Select (@base-ui/react/select). Ported visuals/behavior:
//   - Trigger uses the SAME two-state border model as TextField (recon §1): idle → gray60,
//     open/focus → blue + focus-glow shadow, error → magenta. Radius all12, white fill,
//     all12 content padding (the Flutter dropdown also uses radius all12 + gray60 border).
//   - Trailing chevron-down icon in gray60 that rotates 180° when open (Flutter
//     AnimatedRotation, 300ms; `data-popup-open` drives the rotation here).
//   - Popup: white menu, gray60 1px border, radius all12, soft shadow, max-height ~240px
//     with scroll (Flutter dropdownStyleData maxHeight: 240).
//   - Items: Lato w600 s16 (Flutter h8TextStyle.w600), py-10/px-18 row (matches the Flutter
//     MenuItemStyleData), blue20 highlight on hover/keyboard, blue check on the selected row.
//   - Placeholder text = gray60 (matches TextField hint).
//
// a11y: Base UI wires the trigger/listbox/option roles, keyboard nav, and `aria-*`. The
// label is associated via `Select.Label`, and the trigger carries `aria-invalid` in the
// error state (mirroring TextField). `testId` forwards to the trigger button.
import { useId } from 'react';
import { CSPProvider } from '@base-ui/react/csp-provider';
import { Select as BaseSelect } from '@base-ui/react/select';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';
import { ErrorMessage } from './error-message';

export interface SelectOption {
  /** The option's value (submitted / reported via onValueChange). */
  value: string;
  /** The visible label. Defaults to `value` if omitted. */
  label?: string;
  disabled?: boolean;
}

export interface SelectProps extends TestIdProps {
  /** Options to render in the popup. */
  options: SelectOption[];
  /** Field label (Lato w600 s12, matching TextField). Omit to render no label. */
  label?: string;
  /**
   * Accessible name for the trigger when a VISIBLE `label` cannot carry it.
   *
   * `label` renders STACKED above the field, so a toolbar that puts its caption inline to the left
   * of the control cannot use it. Without either, the trigger's accessible name computes from its
   * content — the current VALUE — so a screen-reader user browsing controls hears "Newest" with no
   * statement of purpose (SC 1.3.1 / 3.3.2). Pass this alongside the visible inline caption.
   */
  'aria-label'?: string;
  /** Placeholder shown when no value is selected (gray60). */
  placeholder?: string;
  /** Controlled value. */
  value?: string | null;
  /** Uncontrolled initial value. */
  defaultValue?: string | null;
  onValueChange?: (value: string | null) => void;
  disabled?: boolean;
  required?: boolean;
  /** Form field name (for native form submission). */
  name?: string;
  /** Error state — switches the trigger border to magenta and (with `errorMessage`) shows the row. */
  error?: boolean;
  /** Error text rendered in an inline <ErrorMessage> beneath the field. */
  errorMessage?: string;
  className?: string;
  /** Optional styling for the portaled popup (for a host's touch-size context). */
  popupClassName?: string;
  /**
   * Visual appearance (added 2026-06-26, ADDITIVE — mirrors Button's appearance prop):
   *   `legacy` (default) → the Flutter-ported dropdown (rounded-all12, blue glow) — unchanged.
   *   `saas`             → the fintech-grade admin select (h-36, neutral border, brand highlight).
   * Same Base UI machinery + a11y in both; only the classes differ. The `role=combobox` trigger,
   * value/placeholder, options, and the `testId` are identical, so existing tests keep passing.
   */
  appearance?: 'legacy' | 'saas';
}

export function Select({
  options,
  label,
  placeholder = 'Select…',
  value,
  defaultValue,
  onValueChange,
  disabled,
  required,
  name,
  error = false,
  errorMessage,
  className,
  popupClassName,
  testId,
  appearance = 'legacy',
  'aria-label': ariaLabel,
}: SelectProps) {
  const reactId = useId();
  const errorId = `${reactId}-error`;
  const hasErrorMessage = error && !!errorMessage;
  const saas = appearance === 'saas';
  const labelFor: Record<string, string> = Object.fromEntries(
    options.map((o) => [o.value, o.label ?? o.value]),
  );

  return (
    <div className={cn('flex flex-col items-stretch', className)}>
      {/*
        `disableStyleElements`: Base UI's popup would otherwise hoist a nonce-less <style> element (its
        scrollbar-hiding rule) on open, which the donate/pay/consent nonce-only `style-src` refuses —
        one console refusal per document. `src/styles/base-ui.css` restates the rule instead; pinned in
        `base-ui-hidden-inputs.test.tsx`.
      */}
      <CSPProvider disableStyleElements>
        <BaseSelect.Root
          value={value}
          defaultValue={defaultValue}
          onValueChange={(next) => onValueChange?.(next as string | null)}
          disabled={disabled}
          required={required}
          name={name}
        >
          {label &&
            (saas ? (
              <BaseSelect.Label className="mb-6 font-sans text-s13 font-w500 tracking-normal text-text-secondary">
                {label}
              </BaseSelect.Label>
            ) : (
              <BaseSelect.Label className="mb-8 font-sans text-s12 font-w600 tracking-givmo text-black">
                {label}
              </BaseSelect.Label>
            ))}
          <BaseSelect.Trigger
            aria-label={ariaLabel}
            aria-invalid={error || undefined}
            aria-describedby={hasErrorMessage ? errorId : undefined}
            className={
              saas
                ? cn(
                    'flex h-36 w-full items-center justify-between gap-8 rounded-md border bg-surface px-12',
                    'font-sans text-s14 font-w500 tracking-normal text-text',
                    'outline-none transition-[box-shadow,border-color]',
                    // SaaS two-state: neutral idle, brand-500 + ring on open/focus, danger on error.
                    // Control boundary — same floor and same pair as Field/Textarea/Checkbox/
                    // SearchInput: neutral-500 is 4.68:1 on white / 4.47:1 on surface-sunken, hover
                    // darkens to neutral-600 (7.54:1) rather than lightening to neutral-400 (2.52:1).
                    'border-neutral-500 hover:border-neutral-600',
                    'data-[popup-open]:border-brand-500 data-[popup-open]:shadow-ring',
                    'focus-visible:border-brand-500 focus-visible:shadow-ring',
                    error &&
                      'border-danger-400 data-[popup-open]:border-danger-400 focus-visible:border-danger-400 focus-visible:shadow-ring-danger',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                  )
                : cn(
                    'flex w-full items-center justify-between gap-8 rounded-all12 border bg-white p-12',
                    'font-sans text-s16 font-w600 tracking-givmo text-black',
                    'outline-none transition-shadow',
                    // Two-state border (recon §1) — idle neutral-500 (the house control boundary;
                    // Flutter's gray60 was 2.65:1, EP-WEB45), open/focus blue + glow, error magenta.
                    'border-neutral-500',
                    'data-[popup-open]:border-blue data-[popup-open]:shadow-focus-glow',
                    'focus-visible:border-blue focus-visible:shadow-focus-glow',
                    error &&
                      'border-magenta data-[popup-open]:border-magenta focus-visible:border-magenta',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                  )
            }
            {...testIdProp(testId)}
          >
            <BaseSelect.Value
              className={
                saas
                  ? 'truncate data-[placeholder]:text-text-placeholder'
                  : 'data-[placeholder]:text-gray60'
              }
            >
              {(v: string | null) => (v == null || v === '' ? placeholder : (labelFor[v] ?? v))}
            </BaseSelect.Value>
            <BaseSelect.Icon
              className={
                saas
                  ? 'flex shrink-0 text-icon-muted transition-transform duration-200 data-[popup-open]:rotate-180'
                  : 'flex text-gray60 transition-transform duration-300 data-[popup-open]:rotate-180'
              }
            >
              <ChevronDown size={saas ? 16 : 21} aria-hidden />
            </BaseSelect.Icon>
          </BaseSelect.Trigger>
          <BaseSelect.Portal>
            <BaseSelect.Positioner
              sideOffset={saas ? 6 : 4}
              alignItemWithTrigger={false}
              className="z-50 outline-none"
            >
              <BaseSelect.Popup
                className={
                  saas
                    ? cn(
                        'max-h-[280px] min-w-[var(--anchor-width)] overflow-y-auto rounded-lg border border-border-subtle bg-surface p-4 shadow-md outline-none',
                        popupClassName,
                      )
                    : cn(
                        'max-h-[240px] min-w-[var(--anchor-width)] overflow-y-auto rounded-all12 border border-gray60 bg-white py-8 shadow-soft outline-none',
                        popupClassName,
                      )
                }
              >
                {options.map((opt) =>
                  saas ? (
                    <BaseSelect.Item
                      key={opt.value}
                      value={opt.value}
                      disabled={opt.disabled}
                      className={cn(
                        'flex cursor-default items-center justify-between gap-8 rounded-md px-12 py-8',
                        'font-sans text-s14 font-w500 tracking-normal text-text',
                        'data-[highlighted]:bg-surface-hover',
                        'data-[selected]:font-w600 data-[selected]:text-brand-700',
                        'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
                        'outline-none',
                      )}
                    >
                      <BaseSelect.ItemText>{opt.label ?? opt.value}</BaseSelect.ItemText>
                      <BaseSelect.ItemIndicator className="flex text-brand-600">
                        <Check size={16} strokeWidth={3} aria-hidden />
                      </BaseSelect.ItemIndicator>
                    </BaseSelect.Item>
                  ) : (
                    <BaseSelect.Item
                      key={opt.value}
                      value={opt.value}
                      disabled={opt.disabled}
                      className={cn(
                        'flex cursor-default items-center justify-between gap-8 px-18 py-10',
                        'font-sans text-s16 font-w600 tracking-givmo text-black',
                        'data-[highlighted]:bg-blue20',
                        'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
                        'outline-none',
                      )}
                    >
                      <BaseSelect.ItemText>{opt.label ?? opt.value}</BaseSelect.ItemText>
                      <BaseSelect.ItemIndicator className="flex text-blue">
                        <Check size={18} strokeWidth={3} aria-hidden />
                      </BaseSelect.ItemIndicator>
                    </BaseSelect.Item>
                  ),
                )}
              </BaseSelect.Popup>
            </BaseSelect.Positioner>
          </BaseSelect.Portal>
        </BaseSelect.Root>
      </CSPProvider>
      {hasErrorMessage && (
        <ErrorMessage testId={testId ? `${testId}-error` : undefined}>
          <span id={errorId}>{errorMessage}</span>
        </ErrorMessage>
      )}
    </div>
  );
}
